import * as React from "react";
import { useEffect, useRef } from "react";
import { styled } from "@mui/material";
import { MessageList } from "@chatscope/chat-ui-kit-react";
import { PatientSummary } from "./PatientSummary";
import { ChatMessages } from "./ChatMessages";
import { CustomMessageInput } from "./UI/CustomMessageInput";
import useSessionStore from "../context/useSessionStore";
import { useEventReportedContext } from "../analytics/EventReporterContext";
import useScrollDetect from "../utils/useScrollDetect";
import { MIX_PANEL_EVENTS } from "../analytics/eventReporter";
import { ChatHistory } from "./ChatHistory";
import ChatContainer from "./UI/ChatContainer";
import { QuestionTypes } from "../context/sessionStore";

//region [[ Styles ]]
const ChatMessagesHolder = styled("div")<{ embedded?: boolean }>(
  ({ theme, embedded }) => ({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    height: "100%",
    width: "100%",
    maxWidth: 800,
    margin: "auto",
    padding: embedded ? "20px 6px 20px 0" : "120px 12px 80px 12px",
    gap: 20,
    "& .scrollbarContainer": {
      height: "100%",
    },
    "& .scrollbarContainer > div": {
      height: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  }),
);

const InputContainer = styled("div")<{ embedded?: boolean }>(
  ({ theme, embedded }) => ({
    borderTop: "1px solid " + theme.palette.divider,
    padding: embedded ? "16px 20px" : "16px 34px 20px 34px",
    [theme.breakpoints.down("md")]: {
      padding: "16px 14px 20px 14px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "10px 14px 12px 14px",
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ChatMain = () => {
  const {
    patientSummary,
    replyIsLoading,
    addQuestion,
    confirmRequired,
    withSuggestions,
    setAutoScroll,
    ragSettings,
  } = useSessionStore();
  const reporter = useEventReportedContext();
  const rtlDisplay = ragSettings?.rtlDisplay;
  const embedded = ragSettings?.embeddedView;
  const showChatHistory = ragSettings?.showChatHistory;
  const msgListRef: React.MutableRefObject<any> = useRef();
  const msgListScrollRef: React.MutableRefObject<Element> = useRef();
  useScrollDetect(msgListScrollRef.current, (scrollAmount) => {
    if (scrollAmount < -25) {
      setAutoScroll(false); //stop autoscroll when user scrolls up
    }
  });

  useEffect(() => {
    msgListScrollRef.current = document
      ?.getElementById("message-list")
      ?.getElementsByClassName("scrollbar-container")[0]; //workaround for getting the scrollbar container from
  }, []);

  const onEnter = (text: string) => {
    if (text.trim()) {
      reporter?.reportEvent(MIX_PANEL_EVENTS.QUESTION, {
        question: text,
        withSuggestions,
      });
      addQuestion(text, (answer) => {
        reporter?.reportAnswer(answer);
      });
    }
  };

  return (
    <ChatContainer embedded={embedded} rtlDisplay={rtlDisplay}>
      <MessageList ref={msgListRef} id={"message-list"}>
        <MessageList.Content>
          <ChatMessagesHolder embedded={embedded} className={"chat-container"}>
            {!confirmRequired && !embedded && <PatientSummary />}
            {showChatHistory && <ChatHistory />}
            {patientSummary && <ChatMessages msgListRef={msgListRef} />}
          </ChatMessagesHolder>
        </MessageList.Content>
      </MessageList>
      <InputContainer embedded={embedded} dir={rtlDisplay ? "rtl" : ""}>
        <CustomMessageInput
          disabled={replyIsLoading(QuestionTypes.Chat) || !patientSummary}
          onSend={onEnter}
        />
      </InputContainer>
    </ChatContainer>
  );
};
